/*
 * Card 305 — the guide's tour photo upload queue, web app edition.
 *
 * A deliberate port of TipDirectApp/util/tourPhotoQueue.js rather than a shared
 * package: the two live in different repos with different module systems, and
 * inventing a shared package for ~120 lines of pure state would cost more than
 * it saves. The drift that matters — the photo cap — is guarded by a test that
 * reads the backend model.
 *
 * Pure functions over a plain array, kept out of the component so they can be
 * tested without rendering anything. startNextUpload is the one exception: it
 * takes its side effects as arguments, for the same reason.
 *
 * Why a queue rather than one batch request: the backend appends each photo to
 * the open session independently, so one photo per request means a failure
 * loses that photo and not the other seven, and it is the only way to show
 * honest per-photo progress.
 */

// Mirrors MAX_PHOTOS_PER_GALLERY in TA-Ticketing's backend
// (src/domain/tap_to_tip/tour_photos/model/index.ts). The cap is necessity
// evidence in the legitimate-interest assessment, not a product preference, so
// the client enforces it too rather than relying on the server to say no.
export const MAX_PHOTOS_PER_GALLERY = 8;

export const STATUS = {
  pending: 'pending',
  uploading: 'uploading',
  done: 'done',
  failed: 'failed'
};

/**
 * How many more photos the guide may add. Anything already failed does not
 * count against the cap: it never reached the gallery, and a guide with flaky
 * wifi must not run out of room having uploaded nothing.
 */
export function remainingSlots(items, uploadedOnServer) {
  const claimed = items.filter(item => item.status !== STATUS.failed).length;
  return Math.max(0, MAX_PHOTOS_PER_GALLERY - uploadedOnServer - claimed);
}

/**
 * Add freshly picked files to the queue, truncating at the cap. Returns the new
 * queue and how many were dropped, so the screen can say "only 3 of those 5
 * fit" rather than silently discarding two.
 */
// Ids come from a counter, not the queue length: once finished photos leave
// the queue the length repeats, and two items sharing an id get each other's
// progress and "done" (same bug as the guide app, card 277, 2026-11-04).
let nextLocalId = 0;

export function enqueue(items, files, uploadedOnServer) {
  const room = remainingSlots(items, uploadedOnServer);
  const accepted = Array.from(files).slice(0, room);

  return {
    items: [
      ...items,
      ...accepted.map((file, index) => ({
        id: `${file.name || 'photo'}-${file.size || 0}-${(nextLocalId += 1)}`,
        file,
        name: file.name || null,
        // Object URLs are revoked by the screen on unmount; holding one per
        // queued photo is what lets the grid show a thumbnail before upload.
        previewUrl: URL.createObjectURL(file),
        status: STATUS.pending,
        progress: 0,
        error: null
      }))
    ],
    rejected: accepted.length === files.length ? 0 : files.length - accepted.length
  };
}

/** The next item the uploader should pick up, or undefined when idle. */
export function nextPending(items) {
  if (items.some(item => item.status === STATUS.uploading)) return undefined;
  return items.find(item => item.status === STATUS.pending);
}

export function markUploading(items, id) {
  return items.map(item =>
    item.id === id
      ? { ...item, status: STATUS.uploading, progress: 0, error: null }
      : item
  );
}

export function markProgress(items, id, progress) {
  const clamped = Math.max(0, Math.min(1, progress));
  return items.map(item => (item.id === id ? { ...item, progress: clamped } : item));
}

/**
 * `serverId` is the publicId the server gave the photo. It is how reconcile
 * knows the server's copy has arrived and this one can go.
 */
export function markDone(items, id, serverId) {
  return items.map(item =>
    item.id === id
      ? { ...item, status: STATUS.done, progress: 1, error: null, serverId: serverId || null }
      : item
  );
}

export function markFailed(items, id, error) {
  return items.map(item =>
    item.id === id
      ? { ...item, status: STATUS.failed, error: error || 'upload_failed' }
      : item
  );
}

/** Retry is an explicit guide action: a failed photo never re-uploads itself. */
export function retry(items, id) {
  return items.map(item =>
    item.id === id
      ? { ...item, status: STATUS.pending, progress: 0, error: null }
      : item
  );
}

export function remove(items, id) {
  return items.filter(item => item.id !== id);
}

/**
 * The queue as the screen should show and count it, given the server's list.
 *
 * A finished photo is in two places for a while: here, and in the server's
 * gallery once the refresh after its upload lands. Showing or counting both
 * drew 8 tiles for 6 photos and told the guide the gallery was full halfway
 * through an upload. So a done item goes as soon as the server holds it, and
 * not before: until the refresh lands, the local copy is the only one there is.
 *
 * A done item with no serverId (a response with no photo in it) can never be
 * matched, so it goes at once rather than hanging around as a ghost.
 *
 * Returns the same array when nothing is dropped, so a state update with the
 * result is a no-op React can bail out of.
 */
/**
 * Add picked files to the queue AS IT IS NOW. A handler that builds on the
 * list it saw when the file chooser opened can write back a photo that has
 * since finished as "uploading", which shows it twice and stalls the next
 * upload behind it for ever (found in the guide app, card 277, 2026-11-04).
 * Use it inside a functional update: setItems(current => addFiles(current, …).items).
 */
export function addFiles(current, files, serverPhotos) {
  const live = reconcile(current, serverPhotos);
  return enqueue(live, files, (serverPhotos || []).length);
}

export function reconcile(items, serverPhotos) {
  const onServer = new Set((serverPhotos || []).map(photo => photo.publicId));
  const kept = items.filter(
    item => item.status !== STATUS.done || (item.serverId && !onServer.has(item.serverId))
  );
  return kept.length === items.length ? items : kept;
}

/** The publicId of the photo an upload added, or null. One photo per request. */
export function uploadedPublicId(result) {
  const photos = (result && result.photos) || [];
  return (photos[0] && photos[0].publicId) || null;
}

/**
 * Start the next pending upload, if there is one. This is the body of the
 * screen's upload effect, kept here so it can be driven without rendering.
 *
 * The screen runs it on every queue change, and marking a photo "uploading" is
 * itself a queue change. So nothing here may belong to a single run: a
 * cleanup-scoped cancel (React runs the cleanup before every re-run) threw
 * every answer away, so the photo sat on "uploading", the gallery and its QR
 * never loaded, and nextPending never offered the next photo. Only leaving the
 * screen (`isMounted` false) stops an answer being applied.
 *
 *   upload(item, onProgress) -> Promise of the server's upload result
 *   afterUpload(result)      -> refresh the server's list (not awaited)
 */
export function startNextUpload({ items, setItems, upload, afterUpload, isMounted }) {
  const next = nextPending(items);
  if (!next) return;

  const live = () => !isMounted || isMounted();

  setItems(current => markUploading(current, next.id));

  upload(next, progress => {
    if (live()) setItems(current => markProgress(current, next.id, progress));
  }).then(
    result => {
      if (!live()) return;
      setItems(current => markDone(current, next.id, uploadedPublicId(result)));
      if (afterUpload) afterUpload(result);
    },
    error => {
      if (live()) setItems(current => markFailed(current, next.id, error && error.message));
    }
  );
}

export function isIdle(items) {
  return !items.some(
    item => item.status === STATUS.pending || item.status === STATUS.uploading
  );
}

export function failedCount(items) {
  return items.filter(item => item.status === STATUS.failed).length;
}

/** Every object URL the queue is holding, so the screen can revoke them. */
export function previewUrls(items) {
  return items.map(item => item.previewUrl).filter(Boolean);
}
